"use client";

import { Check, Crown } from "lucide-react";
import { useRouter, usePathname } from "next/navigation";

import { useStyles } from "./styles";
import { Generation } from "@/types/thread";
import { useLocalStorage } from "@/hooks/useStorage";
import DailyGensSummary from "../MobileLayout/DailyGensSummary";
import GenerationConfig from "../Generation/GenerationConfig";
import PromptViewer from "../Generation/PromptViewer";

interface FirstGenerationViewProps {
  generation: Generation;
  onShowThread: () => void;
}

const FirstGenerationView: React.FC<FirstGenerationViewProps> = ({
  generation,
  onShowThread,
}) => {
  const { classes } = useStyles();
  const firstImage = generation.images[0];
  const router = useRouter();
  const pathname = usePathname();
  const [_, setRedirectPath] = useLocalStorage("redirectPath", "/");

  const handleUpgradeClick = () => {
    setRedirectPath(pathname || "/");
    router.push("/subscribe");
  };

  const handleContinueClick = () => {
    onShowThread();
  };

  if (!firstImage) {
    onShowThread();
    return null;
  }

  return (
    <div className={classes.container}>
      <DailyGensSummary />
      <div className={classes.header}>
        <h2 className={classes.heading}>Awesome! 🎉</h2>
        <p className={classes.subheading}>Here&apos;s your first AI image:</p>
      </div>
      <img
        src={firstImage.url}
        alt={firstImage.alt || "Your first AI image"}
        className={classes.thumbnail}
        style={{
          aspectRatio: generation.aspectRatioUsed.replace(":", " / "),
        }}
      />
      <GenerationConfig
        modelUsed={generation.modelUsed}
        aspectRatio={generation.aspectRatioUsed}
      />
      <PromptViewer
        rawPrompt={generation.prompt}
        textPrompt={generation.textPrompt}
      />
      <div className={classes.offerCard}>
        <div className={classes.offerCardHeader}>
          <h4 className={classes.offerCardHeading}>Premium Offer!</h4>
          <div className={classes.offerCardSaveBadge}>SAVE 40%</div>
        </div>
        <p className={classes.offerCardDescription}>
          Save 40% on any Idyllic premium yearly
        </p>
        <div className={classes.offerCardFeatures}>
          <div className={classes.offerCardFeature}>
            <div className={classes.checkCircle}>
              <Check size={12} />
            </div>
            <p className={classes.offerCardFeatureText}>
              All premium AI models <span>(Valued at $80+)</span>
            </p>
          </div>
          <div className={classes.offerCardFeature}>
            <div className={classes.checkCircle}>
              <Check size={12} />
            </div>
            <p className={classes.offerCardFeatureText}>
              Generate 300+ Images <span>Monthly</span>
            </p>
          </div>
          <div className={classes.offerCardFeature}>
            <div className={classes.checkCircle}>
              <Check size={12} />
            </div>
            <p className={classes.offerCardFeatureText}>
              Instant access, no waiting
            </p>
          </div>
          <div className={classes.offerCardFeature}>
            <div className={classes.checkCircle}>
              <Check size={12} />
            </div>
            <p className={classes.offerCardFeatureText}>
              Image enhance + HD features
            </p>
          </div>
        </div>
      </div>
      <div className={classes.buttonsContainer}>
        <button className={classes.cta} onClick={handleUpgradeClick}>
          <Crown size={18} /> Upgrade to premium
        </button>
        <button
          className={classes.continueButton}
          onClick={handleContinueClick}
        >
          Continue with free plan
        </button>
      </div>
    </div>
  );
};

export default FirstGenerationView;
